import type { Action } from "@judo/actions";
import type { ConfirmDialogOptions } from "@judo/feedback";
import { useModelLabel } from "@judo/i18n";
import type { ActionDefinition, Button as ButtonElement, Column, PageDefinition } from "@judo/model-api";
import { Button, Stack } from "@mui/material";
import {
	type GridFilterModel,
	type GridPaginationModel,
	type GridSortModel,
	useGridApiContext,
} from "@mui/x-data-grid";
import type { Dispatch, SetStateAction } from "react";
import { buildQueryCustomizer } from "../utils/build-query-customizer";
import { getDestructiveConfirmationOptions, isDestructiveAction } from "../utils/destructive-action-utils";
import { findActionByDefinition } from "../utils/find-action-by-definition";
import { getVisibleButtons } from "../utils/get-visible-buttons";
import { IconRenderer } from "./IconRenderer";

export interface TableToolbarProps {
	buttons: ButtonElement[];
	testId: string;
	page?: PageDefinition;
	hasSelection: boolean;
	/** Whether this toolbar belongs to an eager table (aggregated element) */
	isEager?: boolean;
	/** Action dispatcher function (second arg = context overrides, e.g. selectedRows) */
	onDispatch?: (action: Action, contextOverrides?: Record<string, unknown>) => Promise<unknown>;
	/** Currently selected row transfers for bulk operations */
	selectedRows?: unknown[];
	/** Setter for the filter button element, used to anchor the filter panel */
	setFilterButtonEl?: Dispatch<SetStateAction<HTMLButtonElement | null>>;
	/** Current sort model from DataGrid */
	sortModel?: GridSortModel;
	/** Current filter model from DataGrid */
	filterModel?: GridFilterModel;
	/** Current pagination model from DataGrid */
	paginationModel?: GridPaginationModel;
	/** Table columns for filter conversion */
	columns?: Column[];
	/** Pre-computed mask string to include in refresh queryCustomizer */
	mask?: string;
	/** Whether to count records (for pagination display) */
	showTotalCount?: boolean;
	/** Callback to refresh the table after a destructive bulk action (e.g., bulk delete) */
	onAfterBulkDelete?: () => void;
	/** Callback for lazy bulk remove with per-item progress dialog */
	onBulkRemoveWithProgress?: (
		selectedRows: unknown[],
		actionDefinition: ActionDefinition
	) => Promise<boolean | undefined>;
	/** Optional confirmation function for destructive actions */
	confirm?: (options: ConfirmDialogOptions) => Promise<boolean>;
}

// Module augmentation to allow custom props in DataGrid toolbar slot
declare module "@mui/x-data-grid" {
	interface ToolbarPropsOverrides extends TableToolbarProps {}
}

/**
 * Custom toolbar component for table actions.
 * Filters buttons by page actions and bulk action requirements.
 * Used as a DataGrid slots.toolbar component.
 *
 * Note: Uses Stack instead of GridToolbarContainer to avoid CSS import
 * issues in test environments while maintaining proper toolbar styling.
 */
export function TableToolbar({
	buttons,
	testId,
	page,
	hasSelection,
	isEager,
	onDispatch,
	selectedRows,
	setFilterButtonEl,
	sortModel,
	filterModel,
	paginationModel,
	columns,
	mask,
	showTotalCount,
	onAfterBulkDelete,
	onBulkRemoveWithProgress,
	confirm,
}: TableToolbarProps) {
	// Access DataGrid API for programmatic control (e.g., opening filter panel)
	const apiRef = useGridApiContext();

	const handleButtonClick = async (button: ButtonElement) => {
		const actionType = button.actionDefinition?.["@type"];

		// Handle FilterActionDefinition specially - open the DataGrid's built-in filter panel
		// This applies to both FilterActionDefinition (table filter) and FilterRelationActionDefinition (relation filter)
		if (actionType === "FilterActionDefinition" || actionType === "FilterRelationActionDefinition") {
			apiRef.current.showFilterPanel();
			return;
		}

		// For lazy bulk remove, use combined confirmation + progress dialog (skips separate confirm)
		if (actionType === "BulkRemoveActionDefinition" && !isEager && onBulkRemoveWithProgress && selectedRows?.length) {
			try {
				const result = await onBulkRemoveWithProgress(selectedRows, button.actionDefinition!);
				// undefined = user cancelled in dialog, don't refresh
				if (result != null) {
					onAfterBulkDelete?.();
				}
			} catch {
				// Dialog error — still refresh in case partial removes succeeded
				onAfterBulkDelete?.();
			}
			return;
		}

		// Confirmation check for destructive actions
		if (confirm && isDestructiveAction(actionType)) {
			const confirmed = await confirm(getDestructiveConfirmationOptions(actionType!));
			if (!confirmed) return;
		}

		const action = page ? findActionByDefinition(page, button.actionDefinition) : undefined;

		if (action && onDispatch) {
			// For refresh actions on non-eager tables, include current sort/filter/pagination state
			if (
				actionType === "RefreshActionDefinition" &&
				!isEager &&
				sortModel &&
				filterModel &&
				paginationModel &&
				columns
			) {
				const queryCustomizer = buildQueryCustomizer({
					sortModel,
					paginationModel,
					filterModel,
					columns,
					mask,
				});

				const actionWithQueryCustomizer = {
					...action,
					queryCustomizer,
					...(showTotalCount ? { countRecords: true } : {}),
				};

				onDispatch(actionWithQueryCustomizer).catch((error) => {
					console.error("Toolbar action failed:", error);
				});
				return;
			}

			// For bulk actions, pass selectedRows as context override so handlers can access them
			const isBulkAction = actionType === "BulkDeleteActionDefinition" || actionType === "BulkRemoveActionDefinition";

			if (isBulkAction) {
				const bulkOverrides = selectedRows ? { selectedRows } : undefined;
				const dispatchPromise = onDispatch(action, bulkOverrides);

				// For lazy tables, refresh after the bulk action completes
				if (!isEager && onAfterBulkDelete) {
					dispatchPromise
						.then(() => {
							onAfterBulkDelete();
						})
						.catch((error) => {
							console.error("Toolbar action failed:", error);
						});
				} else {
					dispatchPromise.catch((error) => {
						console.error("Toolbar action failed:", error);
					});
				}
				return;
			}

			// For clear actions on lazy tables, refresh after the clear completes
			if (actionType === "ClearActionDefinition" && !isEager && onAfterBulkDelete) {
				onDispatch(action)
					.then(() => {
						onAfterBulkDelete();
					})
					.catch((error) => {
						console.error("Toolbar action failed:", error);
					});
				return;
			}

			onDispatch(action).catch((error) => {
				console.error("Toolbar action failed:", error);
			});
		}
	};

	// Filter buttons: first by page actions (using getVisibleButtons), then by bulk requirement and eager table rules
	const pageFilteredButtons = getVisibleButtons(buttons, page?.actions);
	const visibleButtons = pageFilteredButtons.filter((button) => {
		// Hide refresh actions for eager tables - they are aggregated elements managed by their owner
		if (isEager && button.actionDefinition?.["@type"] === "RefreshActionDefinition") {
			return false;
		}
		// Check the button's actionDefinition for isBulk property
		if (button.actionDefinition?.isBulk) {
			return hasSelection; // Only show bulk actions when rows are selected
		}
		return true; // Always show non-bulk actions
	});

	// Helper to check if a button is a filter button
	const isFilterButton = (button: ButtonElement): boolean => {
		const actionType = button.actionDefinition?.["@type"];
		return actionType === "FilterActionDefinition" || actionType === "FilterRelationActionDefinition";
	};

	// Don't render toolbar if no visible buttons
	if (visibleButtons.length === 0) {
		return null;
	}

	return (
		<Stack
			direction="row"
			spacing={1}
			data-testid={`${testId}::toolbar`}
			sx={{
				px: 1,
				py: 0.5,
				minHeight: 52,
				alignItems: "center",
				borderBottom: 1,
				borderColor: "divider",
			}}
		>
			{visibleButtons.map((button) => (
				<ToolbarButton
					key={button["xmi:id"]}
					button={button}
					onClick={() => handleButtonClick(button)}
					filterRef={isFilterButton(button) && setFilterButtonEl ? setFilterButtonEl : undefined}
				/>
			))}
		</Stack>
	);
}

/** Toolbar button with translated label via useModelLabel. */
function ToolbarButton({
	button,
	onClick,
	filterRef,
}: {
	button: ButtonElement;
	onClick: () => void;
	filterRef?: React.Ref<HTMLButtonElement>;
}) {
	const { label } = useModelLabel(button);
	return (
		<Button
			size="small"
			variant={(button.buttonStyle as "contained" | "outlined" | "text") ?? "contained"}
			onClick={onClick}
			disabled={button.disabled}
			startIcon={button.icon ? <IconRenderer icon={button.icon} /> : undefined}
			ref={filterRef}
		>
			{label}
		</Button>
	);
}
